import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useLanguage } from "../i18n";

export const Projects = () => {
  const { t } = useLanguage();
  return (
    <section id="projects" data-testid="projects-section" className="relative py-28 md:py-40 overflow-hidden">
      <span className="absolute -top-8 left-4 md:left-10 font-display font-bold text-[26vw] md:text-[18rem] leading-none text-white/[0.03] select-none pointer-events-none" aria-hidden="true">
        03
      </span>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-mono text-xs uppercase tracking-[0.3em] text-brand-cyan mb-6" data-testid="projects-chapter-label">
          {t.chapters.projects}
        </motion.p>
        <div className="max-w-2xl mb-16 md:mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-semibold tracking-tight text-4xl md:text-5xl leading-[1.05]"
            data-testid="projects-title"
          >
            {t.projects.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 font-mono text-sm text-white/50 leading-relaxed"
            data-testid="projects-subtitle"
          >
            {t.projects.sub}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-testid="projects-grid">
          {t.projects.items.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.15, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`project-card-${i}`}
              className={`group relative ${i % 2 === 1 ? "md:mt-16" : ""}`}
            >
              <div className="relative rounded-lg overflow-hidden border border-white/5 transition-[border-color,box-shadow] duration-500 group-hover:border-brand-green/30 group-hover:shadow-[0_0_50px_-12px_rgba(0,255,102,0.2)]">
                <div className="absolute inset-0 z-10 bg-gradient-to-t from-ink via-ink/20 to-transparent pointer-events-none" />
                <div className="overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute z-20 bottom-0 left-0 right-0 p-6 md:p-8 flex items-end justify-between gap-4">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-cyan">{p.cat}</span>
                    <h3 className="mt-2 font-display font-medium text-xl md:text-2xl tracking-tight">{p.title}</h3>
                    <p className="mt-2 font-mono text-xs text-white/50 leading-relaxed max-w-md">{p.desc}</p>
                  </div>
                  <span className="shrink-0 w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/50 transition-all duration-500 group-hover:bg-brand-cyan group-hover:text-ink group-hover:border-brand-cyan">
                    <ArrowUpRight size={18} weight="bold" />
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 flex justify-center"
        >
          <a
            href="https://www.behance.net/lucapedano"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="behance-button"
            className="group inline-flex items-center gap-3 rounded-full border border-brand-cyan/40 bg-brand-cyan/5 px-8 py-4 font-mono text-xs uppercase tracking-widest text-brand-cyan hover:bg-brand-cyan hover:text-ink transition-colors duration-300"
          >
            {t.projects.behance}
            <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
